import {useNavigate, useParams} from "react-router-dom";
import '../../styles/Product.scss';
import {useEffect, useState} from "react";
import {IndividualProductData, ProductSizes, Product} from "../types/Page";
import api from "../../api";
import Header from "./Components/Header";
import FooterWithoutSignup from "./Components/FooterWithoutSignup";
import {IMAGE_URL} from "../../path";


function IndividualProduct() {
    const {slug} = useParams();

    const [productData, setProductData] =
        useState<IndividualProductData | null>(null);

    const [activeTab, setActiveTab] =
        useState("about");

    const [activeCapacity, setActiveCapacity] =
        useState<ProductSizes | undefined>(
            undefined
        );

    /*
     * Related products carousel.
     *
     * On mobile the structure becomes:
     *
     * [last] [product 1] [product 2] [product 3] [first]
     *
     * So index 1 is the first real product.
     */
    const [activeRelatedIndex, setActiveRelatedIndex] =
        useState(1);

    const [expandedRelatedProduct, setExpandedRelatedProduct] =
        useState<Product | null>(null);

    const [selectedRelatedCaps, setSelectedRelatedCaps] =
        useState<Record<number, number>>({});

    /*
     * Used when jumping from a cloned product back to
     * the corresponding real product.
     */
    const [relatedTransitionEnabled, setRelatedTransitionEnabled] =
        useState(true);

    const navigate = useNavigate();


    /*
     * Set the initial capacity for the main product.
     */
    useEffect(() => {
        if (productData?.product.sizes?.length) {
            setActiveCapacity(
                productData.product.sizes[0]
            );
        }
    }, [productData]);


    /*
     * Reset the related carousel whenever the main
     * product changes.
     */
    useEffect(() => {
        const count =
            productData?.related_products?.length ?? 0;

        if (count > 1) {
            setActiveRelatedIndex(1);
        } else {
            setActiveRelatedIndex(0);
        }

        setExpandedRelatedProduct(null);
        setSelectedRelatedCaps({});
        setRelatedTransitionEnabled(true);
    }, [productData?.product?.id]);


    /*
     * Re-enable the CSS transition after jumping
     * from a clone to the real product.
     */
    useEffect(() => {
        if (!relatedTransitionEnabled) {
            const frame = requestAnimationFrame(() => {
                setRelatedTransitionEnabled(true);
            });

            return () => {
                cancelAnimationFrame(frame);
            };
        }
    }, [relatedTransitionEnabled]);


    /*
     * Start tracking the user's swipe.
     */
    const handleRelatedTouchStart = (
        e: React.TouchEvent<HTMLDivElement>
    ) => {
        e.currentTarget.dataset.touchStartX =
            e.touches[0].clientX.toString();
    };


    /*
     * Handle the user's swipe.
     */
    const handleRelatedTouchEnd = (
        e: React.TouchEvent<HTMLDivElement>
    ) => {
        const startX = Number(
            e.currentTarget.dataset.touchStartX
        );

        const endX = e.changedTouches[0].clientX;

        const difference = startX - endX;

        /*
         * Ignore taps / small movements.
         */
        if (Math.abs(difference) < 50) {
            return;
        }

        const count =
            productData?.related_products?.length ?? 0;

        /*
         * Nothing to swipe if there aren't at least
         * two related products.
         */
        if (count < 2) {
            return;
        }

        /*
         * Close an expanded card when swiping.
         */
        setExpandedRelatedProduct(null);

        if (difference > 0) {
            /*
             * Swipe left.
             *
             * Example with 3 products:
             *
             * 0 = cloned P3
             * 1 = P1
             * 2 = P2
             * 3 = P3
             * 4 = cloned P1
             */
            setActiveRelatedIndex((current) =>
                Math.min(
                    current + 1,
                    count + 1
                )
            );
        } else {
            /*
             * Swipe right.
             */
            setActiveRelatedIndex((current) =>
                Math.max(
                    current - 1,
                    0
                )
            );
        }
    };


    /*
     * When the carousel reaches a clone, allow the
     * animation to finish and then instantly jump
     * to the corresponding real product.
     *
     * Example:
     *
     * P3 | P1 | P2 | P3 | P1
     *  0    1    2    3    4
     *
     * At 0 -> jump to 3
     * At 4 -> jump to 1
     */
    const handleRelatedTransitionEnd = () => {
        const count =
            productData?.related_products?.length ?? 0;

        if (count < 2) {
            return;
        }

        /*
         * We reached the cloned last product.
         */
        if (activeRelatedIndex === 0) {
            setRelatedTransitionEnabled(false);

            setActiveRelatedIndex(count);

            return;
        }

        /*
         * We reached the cloned first product.
         */
        if (
            activeRelatedIndex ===
            count + 1
        ) {
            setRelatedTransitionEnabled(false);

            setActiveRelatedIndex(1);
        }
    };


    /*
     * Expand / collapse a related product.
     */
    const toggleExpandedRelatedProduct = (
        product: Product
    ) => {
        setExpandedRelatedProduct((current) =>
            current?.id === product.id
                ? null
                : product
        );
    };


    /*
     * Fetch product data.
     */
    async function fetchProductData() {
        try {
            const res = await api.get(
                `/v1/product/${slug}`
            );

            setProductData(res.data);
        } catch (e) {
            console.error(e);
        }
    }


    useEffect(() => {
        fetchProductData();
    }, [slug]);


    /*
     * Create the mobile carousel list.
     *
     * With 3 products:
     *
     * [P3, P1, P2, P3, P1]
     *
     * The clones are hidden on desktop via SCSS.
     */
    const relatedProducts =
        productData?.related_products ?? [];

    const relatedProductsWithClones =
        relatedProducts.length > 1
            ? [
                relatedProducts[
                relatedProducts.length - 1
                    ],
                ...relatedProducts,
                relatedProducts[0],
            ]
            : relatedProducts;


    return (
        <div>

            <Header />

            {productData &&
                productData.product && (

                    <div>

                        <div className="product-main">

                            <div className="product-main-info">

                                <h2 className="product-range">
                                    {
                                        productData.product
                                            .range.name
                                    }{" "}
                                    Range
                                </h2>

                                <h1 className="product-name">
                                    {
                                        productData.product
                                            .name
                                    }
                                </h1>

                                <p className="product-description">
                                    {
                                        productData.product
                                            .description
                                    }
                                </p>


                                <div className="product-information">

                                    <div className="product-information-tab-headers">

                                        <div
                                            className={`product-information-tab-header ${
                                                activeTab ===
                                                "about"
                                                    ? "active"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                setActiveTab(
                                                    "about"
                                                )
                                            }
                                        >

                                            <div className="header">
                                                About
                                            </div>

                                            <div className="span">
                                                {activeTab ===
                                                "about"
                                                    ? "-"
                                                    : "+"}
                                            </div>

                                        </div>


                                        <div
                                            className={`product-information-tab-header ${
                                                activeTab ===
                                                "ingredients"
                                                    ? "active"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                setActiveTab(
                                                    "ingredients"
                                                )
                                            }
                                        >

                                            <div className="header">
                                                Ingredients
                                            </div>

                                            <div className="span">
                                                {activeTab ===
                                                "ingredients"
                                                    ? "-"
                                                    : "+"}
                                            </div>

                                        </div>

                                    </div>


                                    <div
                                        className="product-information-main"
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                activeTab ===
                                                "about"
                                                    ? productData
                                                        .product
                                                        .about
                                                    : productData
                                                        .product
                                                        .ingredients,
                                        }}
                                    />

                                </div>


                                <div className="product-retailers">

                                    <h3>
                                        Where to purchase
                                    </h3>

                                    <div className="retailers-inner">

                                        {productData.retailers.map(
                                            (retailer) => (
                                                <div
                                                    className="retailer"
                                                    key={
                                                        retailer.name
                                                    }
                                                    onClick={() =>
                                                        window.open(
                                                            retailer.link,
                                                            "_blank",
                                                            "noopener,noreferrer"
                                                        )
                                                    }
                                                >

                                                    <img
                                                        src={`${IMAGE_URL}/${retailer.logo}`}
                                                        alt={
                                                            retailer.name
                                                        }
                                                    />

                                                </div>
                                            )
                                        )}

                                    </div>

                                </div>

                            </div>


                            <div className="product-main-image">

                                <div
                                    className="product-image-backing"
                                    style={
                                        {
                                            "--product-background-color":
                                            productData
                                                .product
                                                .color_code,
                                        } as React.CSSProperties
                                    }
                                >

                                    <img
                                        src={`${IMAGE_URL}/${
                                            activeCapacity?.pack_shot ??
                                            productData
                                                .product
                                                .sizes[0]
                                                ?.pack_shot
                                        }`}
                                        alt={
                                            productData.product
                                                .name
                                        }
                                    />

                                </div>


                                <div className="product-sizes">

                                    <h3 className="sizes-head">

                                        {
                                            activeCapacity?.title ??
                                            productData
                                                .product
                                                .sizes[0]
                                                ?.title
                                        }{" "}

                                        (
                                        {
                                            activeCapacity?.capacity ??
                                            productData
                                                .product
                                                .sizes[0]
                                                ?.capacity
                                        }
                                        )

                                    </h3>


                                    <div className="sizes-inner">

                                        {productData.product.sizes.map(
                                            (size) => (
                                                <div
                                                    key={size.id}
                                                    className={`size ${
                                                        (
                                                            activeCapacity?.id ??
                                                            productData
                                                                .product
                                                                .sizes[0]
                                                                ?.id
                                                        ) ===
                                                        size.id
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        setActiveCapacity(
                                                            size
                                                        )
                                                    }
                                                >
                                                    {
                                                        size.capacity
                                                    }
                                                </div>
                                            )
                                        )}

                                    </div>

                                </div>

                            </div>

                        </div>


                        {/* RELATED PRODUCTS */}

                        <div className="related">

                            <div className="related-head">

                                <div className="text">
                                    You May also like these
                                </div>

                            </div>


                            <div
                                className="related-products"
                                onTouchStart={
                                    handleRelatedTouchStart
                                }
                                onTouchEnd={
                                    handleRelatedTouchEnd
                                }
                            >

                                <div
                                    className="related-product-grid"
                                    onTransitionEnd={(e) => {
                                        // Only respond to the carousel track's own transform transition.
                                        // Ignore transitions bubbling up from individual product cards.
                                        if (
                                            e.target !== e.currentTarget ||
                                            e.propertyName !== "transform"
                                        ) {
                                            return;
                                        }

                                        handleRelatedTransitionEnd();
                                    }}
                                    style={
                                        {
                                            "--related-offset":
                                                `-${
                                                    activeRelatedIndex *
                                                    70 -
                                                    15
                                                }vw`,
                                            "--related-transition":
                                                relatedTransitionEnabled
                                                    ? "transform 0.4s ease"
                                                    : "none",
                                        } as React.CSSProperties
                                    }
                                >

                                    {relatedProductsWithClones.map(
                                        (
                                            product,
                                            index
                                        ) => {

                                            const isClone =
                                                relatedProducts.length >
                                                1 &&
                                                (
                                                    index ===
                                                    0 ||
                                                    index ===
                                                    relatedProductsWithClones.length -
                                                    1
                                                );


                                            const selectedCap =
                                                selectedRelatedCaps[
                                                    product.id
                                                    ] ?? 0;


                                            const selectedSize =
                                                product.sizes[
                                                    selectedCap
                                                    ] ??
                                                product.sizes[0];


                                            const isExpanded =
                                                expandedRelatedProduct?.id ===
                                                product.id;


                                            return (

                                                <div
                                                    key={`${product.id}-${index}`}
                                                    onClick={() =>
                                                        navigate(
                                                            `/product/${product.slug}`
                                                        )
                                                    }
                                                    className={`related-product ${
                                                        isClone
                                                            ? "related-product-clone"
                                                            : ""
                                                    } ${
                                                        isExpanded
                                                            ? "expanded"
                                                            : ""
                                                    }`}
                                                    style={
                                                        {
                                                            "--product-background-color":
                                                            product.color_code,
                                                        } as React.CSSProperties
                                                    }
                                                >

                                                    <img
                                                        src={`${IMAGE_URL}/${selectedSize.pack_shot}`}
                                                        alt={
                                                            product.name +
                                                            selectedSize.capacity
                                                        }
                                                    />


                                                    <div className="product-info">

                                                        <h2>
                                                            {
                                                                product.name
                                                            }
                                                        </h2>


                                                        <h3>
                                                            {
                                                                selectedSize.title
                                                            }{" "}
                                                            (
                                                            {
                                                                selectedSize.capacity
                                                            }
                                                            )
                                                        </h3>


                                                        {isExpanded && (
                                                            <div>

                                                                <p>
                                                                    {
                                                                        product.description
                                                                    }
                                                                </p>


                                                                <div className="capacities">

                                                                    {product.sizes.map(
                                                                        (
                                                                            size,
                                                                            index
                                                                        ) => (

                                                                            <div
                                                                                key={
                                                                                    size.id
                                                                                }
                                                                                className={`capacities-item ${
                                                                                    selectedCap ===
                                                                                    index
                                                                                        ? "active"
                                                                                        : ""
                                                                                }`}
                                                                                onClick={(
                                                                                    e
                                                                                ) => {

                                                                                    e.stopPropagation();

                                                                                    setSelectedRelatedCaps(
                                                                                        (
                                                                                            prev
                                                                                        ) => ({
                                                                                            ...prev,
                                                                                            [product.id]:
                                                                                            index,
                                                                                        })
                                                                                    );

                                                                                }}
                                                                            >
                                                                                {
                                                                                    size.capacity
                                                                                }
                                                                            </div>

                                                                        )
                                                                    )}

                                                                </div>

                                                            </div>
                                                        )}


                                                        <div
                                                            className="bottom-icon"
                                                            onClick={(
                                                                e
                                                            ) => {

                                                                e.stopPropagation();

                                                                toggleExpandedRelatedProduct(
                                                                    product
                                                                );

                                                            }}
                                                        >
                                                            {
                                                                isExpanded
                                                                    ? "-"
                                                                    : "+"
                                                            }
                                                        </div>

                                                    </div>

                                                </div>

                                            );

                                        }
                                    )}

                                </div>

                            </div>

                        </div>

                    </div>

                )}


            <FooterWithoutSignup />

        </div>
    );
}


export default IndividualProduct;
